import reporter from '../report';
import { mktBridgeCallHandler } from '@/utils/instantLoaded/toolsWithoutDeps';
import createRequest from '@/services/createRequest';
import { saasDomainInterceptor } from '@/services/interceptors';
import { UseLiveChatSDKApp, isBank } from '@/config/core';
import { safeGetLiveChatSDKHttpHeaders } from '@/utils/liveChatToken';

enum BridgeError {
  // SDK error (from main app)
  SDKError = -1,
  // Success
  Success = 0,
  // Failed
  Failed = 1,
}

type BridgeRes = {
  error: BridgeError;
  status: number;
  errorMessage: string;
  data: any;
};

const fetch = createRequest({ baseURL: '/chat/v2', timeout: 10000 });
fetch?.interceptors?.request?.use?.(async (config) => {
  if (UseLiveChatSDKApp.is() || isBank()) {
    const tokens = await safeGetLiveChatSDKHttpHeaders();
    Object.entries(tokens).map(([key, value]) => {
      config.headers.common[key] = value || '';
    });
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  config.requestStartTime = Date.now();
  return config;
});
fetch?.interceptors?.request?.use?.(saasDomainInterceptor);

/**
 * 存储持久化数据到native的disk（由于localstorage存在被native清除的场景，所以需要存一份到native那边）
 *
 * @export
 * @param {string} key
 * @param {Record<string, any>} data
 * @returns
 */
export async function saveDataToLocalStorage(key: string, data: Record<string, any> | string, options?: {
  useCache?: boolean;
}): Promise<void> {

  const { useCache = true } = (options || {});

  let saveData = data;
  try {
    if (typeof saveData !== 'string') {
      saveData = JSON.stringify(data);
    }
    window.localStorage.setItem(key, saveData);
    if (useCache) {
      await fetch({
        url: '/write_cache',
        method: 'post',
        data: {
          key,
          data: saveData,
        },
      });
    }
    // 兜底，避免api失败
    await mktBridgeCallHandler('save', {
      key: key,
      data: saveData,
      persist: 1, // 当如disk，持久化存储
    });
  } catch (error) {
    // 上报日志，存储失败
    reporter.apiLog('save Data fail', { level: 'warn', error: JSON.stringify(error) });
  }
}

/**
 * 从持久化存储中获取数据优先从localStorage中获取，如果没有尝试从native获取
 *
 * @export
 * @param {string} key
 * @returns
 */
export async function getDataFromLocalStorage(key: string) {
  try {
    const localStorageData = window.localStorage.getItem(key);
    // @ts-ignore
    if (localStorageData) {
      return localStorageData;
    }

    const remoteResult = await fetch({
      url: '/read_cache',
      method: 'get',
      params: { key },
    });

    reporter.apiLog(`[Container] data for ${key}: ${JSON.stringify(remoteResult)}`);

    const { data } = remoteResult;
    if (data && data.code === 0 && data.data) {
      return data.data.data;
    }

    const localResult = (await mktBridgeCallHandler('load', { key: key })) as BridgeRes;
    if (localResult && localResult.status === 1) {
      return localResult?.data;
    }
    return null;
  } catch (error) {
    reporter.apiLog('get Data fail', { level: 'warn', error: JSON.stringify(error) });
    return null;
  }
}

/**
 * 清除持久化存储
 *
 * @export
 * @param {string} key
 * @returns
 */
export async function clearDataFromLocalStorage(key: string) {
  try {
    window.localStorage.removeItem(key);
    await fetch({
      url: '/clear_cache',
      method: 'post',
      data: { key },
    });
    await mktBridgeCallHandler('clear', { key });
  } catch (error) {
    reporter.apiLog('clear Data fail', { level: 'warn', key: key, error: JSON.stringify(error) });
  }
}
